import {useCallback, useEffect, useRef, useState} from 'react';

interface UseSectionActiveTrackerOptions {
  /** Id du conteneur DOM dont les enfants sont les sections */
  containerId: string;
  /** Liste (stable) des catégories à suivre. Doit être mémoïsée côté appelant. */
  categories: readonly {categorie_id: string}[];
  /** En mode drawer, on écoute le scroll du conteneur lui-même plutôt que celui du window */
  useDrawerScrolling: boolean;
  /** Distance en pixels sous le top du scroll root au-dessus de laquelle une section est considérée "en cours de lecture" */
  headerOffset: number;
}

interface UseSectionActiveTrackerResult {
  /** Id de la section actuellement en cours de lecture (ou `null` si aucune) */
  activeSectionId: string | null;
  /** Permet de forcer manuellement la section active (ex: mise à jour optimiste au clic) */
  setActiveSectionId: (id: string | null) => void;
  /** Suspend le tracking automatique pendant `ms` millisecondes — utile pendant une animation de scroll manuelle pour éviter que le tracking n'écrase la sélection */
  blockTrackingForMs: (ms: number) => void;
}

/**
 * Suit quelle section est "en cours de lecture" dans un conteneur scrollable.
 *
 * Algorithme : au scroll, on parcourt les sections dans l'ordre DOM et on prend
 * la dernière dont le top a franchi la ligne de référence (top du scroll root +
 * headerOffset). C'est déterministe : indépendant de la hauteur des sections,
 * et ne souffre pas du "petite section en bas bat grande section en haut" de
 * l'IntersectionObserver trié par ratio.
 *
 * Déclencheurs du recalcul : scroll, resize, et mutation des enfants du
 * conteneur (pour gérer l'apparition/disparition de sections après filtrage).
 */
export const useSectionActiveTracker = ({
  containerId,
  categories,
  useDrawerScrolling,
  headerOffset,
}: UseSectionActiveTrackerOptions): UseSectionActiveTrackerResult => {
  const [activeSectionId, setActiveSectionId] = useState<string | null>(null);

  // Miroir du state pour éviter de recréer les listeners au changement d'activeSectionId
  const activeSectionIdRef = useRef<string | null>(null);
  useEffect(() => {
    activeSectionIdRef.current = activeSectionId;
  }, [activeSectionId]);

  // Flag de suspension du tracking (activé pendant une animation de scroll déclenchée au clic)
  const trackingBlockedRef = useRef<boolean>(false);
  const blockTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const blockTrackingForMs = useCallback((ms: number) => {
    trackingBlockedRef.current = true;
    if (blockTimerRef.current) clearTimeout(blockTimerRef.current);
    blockTimerRef.current = setTimeout(() => {
      trackingBlockedRef.current = false;
      blockTimerRef.current = null;
    }, ms);
  }, []);

  // Cleanup du timer à l'unmount pour éviter un callback sur un composant démonté
  useEffect(
    () => () => {
      if (blockTimerRef.current) clearTimeout(blockTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (categories.length === 0) return;

    // Snapshot des IDs de catégorie pour lookup O(1)
    const catIds = new Set(categories.map((c) => c.categorie_id));

    // En mode drawer, le conteneur des sections est lui-même le scrollable
    // (cf. `cadencier_body_container_virtual` côté backoffice avec overflow:auto).
    // En mode standard, on écoute le scroll du window.
    const scrollRoot: HTMLElement | Window = useDrawerScrolling
      ? (document.getElementById(containerId) ?? window)
      : window;

    const updateActiveSection = () => {
      if (trackingBlockedRef.current) return;
      const container = document.getElementById(containerId);
      if (!container) return;
      const sections = Array.from(container.children).filter(
        (el): el is HTMLElement =>
          el instanceof HTMLElement && catIds.has(el.id),
      );
      if (sections.length === 0) return;

      // Ligne de référence : top du viewport en standard, top du container en drawer
      const referenceTop =
        scrollRoot instanceof Window
          ? headerOffset
          : scrollRoot.getBoundingClientRect().top + headerOffset;

      // Dernière section dont le top a franchi la ligne de référence
      let current: string = sections[0].id;
      for (const section of sections) {
        if (section.getBoundingClientRect().top - referenceTop <= 0) {
          current = section.id;
        } else {
          break;
        }
      }
      if (activeSectionIdRef.current !== current) setActiveSectionId(current);
    };

    // Throttling via requestAnimationFrame : au plus un recalcul par frame
    let rafId: number | null = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        rafId = null;
        updateActiveSection();
      });
    };

    // Calcul initial (au cas où le scroll est déjà en cours / sections ont changé)
    updateActiveSection();

    // Chaque fois que l'utilisateur scrolle dans scrollRoot, on recalcule
    scrollRoot.addEventListener('scroll', onScroll, {passive: true});
    // Chaque fois que la fenêtre est redimensionnée, les positions changent
    window.addEventListener('resize', onScroll, {passive: true});

    // Relance le calcul quand les sections apparaissent/changent après filtrage
    const container = document.getElementById(containerId);
    const mutationObserver = container
      ? new MutationObserver(() => {
          updateActiveSection();
        })
      : null;
    mutationObserver?.observe(container!, {childList: true});

    return () => {
      scrollRoot.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      mutationObserver?.disconnect();
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, [categories, containerId, headerOffset, useDrawerScrolling]);

  return {activeSectionId, setActiveSectionId, blockTrackingForMs};
};
